import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  CircularProgress,
  IconButton,
  Box,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { farmPlotService } from '../../services/farmPlotService';
import type { FarmPlot } from '../../types/farmPlots';

interface FarmPlotSelectCompactProps {
  farmId: number | null;
  value: number | null;
  onChange: (plotId: number | null, plotName: string) => void;
  disabled?: boolean;
  error?: boolean;
  onRequestCreatePlot?: () => void;
  /** Optional externally managed plots list (for shared state across multiple selectors) */
  plots?: FarmPlot[];
  /** Optional loading state when plots are externally managed */
  externalLoading?: boolean;
}

/**
 * Compact farm plot selector for use in tables.
 * Uses MUI Autocomplete with size="small" and variant="standard".
 */
export const FarmPlotSelectCompact: React.FC<FarmPlotSelectCompactProps> = ({
  farmId,
  value,
  onChange,
  disabled = false,
  error = false,
  onRequestCreatePlot,
  plots: externalPlots,
  externalLoading = false,
}) => {
  const [internalPlots, setInternalPlots] = useState<FarmPlot[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);

  // Use external plots if provided, otherwise use internal state
  const plots = externalPlots ?? internalPlots;
  const loading = externalPlots ? externalLoading : internalLoading;

  // Load farm plots when farmId changes (only if not using external plots)
  const loadFarmPlots = useCallback(async () => {
    if (externalPlots !== undefined) return; // Skip if using external plots
    if (!farmId) {
      setInternalPlots([]);
      return;
    }

    setInternalLoading(true);
    try {
      const data = await farmPlotService.getByFarm(farmId);
      setInternalPlots(data);
    } catch {
      // Failed to load farm plots - continue with empty list
      setInternalPlots([]);
    } finally {
      setInternalLoading(false);
    }
  }, [farmId, externalPlots]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- loadFarmPlots sets loading/empty state synchronously before an async fetch keyed on farmId; refactor is not behavior-preserving-trivial
    loadFarmPlots();
  }, [loadFarmPlots]);

  // Find selected plot
  const selectedPlot = useMemo(() => {
    return plots.find(plot => plot.idFarmPlot === value) || null;
  }, [plots, value]);

  // Handle plot selection
  const handleChange = (_: React.SyntheticEvent, newValue: FarmPlot | null) => {
    if (newValue) {
      onChange(newValue.idFarmPlot, newValue.plotName);
    } else {
      onChange(null, '');
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Autocomplete
        value={selectedPlot}
        onChange={handleChange}
        options={plots}
        getOptionLabel={(option) => option.plotName}
        isOptionEqualToValue={(option, val) => option.idFarmPlot === val.idFarmPlot}
        loading={loading}
        disabled={disabled || !farmId}
        size="small"
        sx={{ flex: 1, minWidth: 120 }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={!farmId ? "Select farm first" : "Select plot"}
            variant="standard"
            error={error}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
      {!disabled && farmId && onRequestCreatePlot && (
        <IconButton
          size="small"
          onClick={onRequestCreatePlot}
          title="Create new plot"
          sx={{ p: 0.5 }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

